import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATS_PASSWORD = '090909';

const StatsPage = () => {
  const [counters, setCounters] = useState([]);
  const [visits, setVisits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Password gate
  const [unlocked, setUnlocked] = useState(() => {
    try {
      return sessionStorage.getItem('stiltech_stats_unlocked') === '1';
    } catch { return false; }
  });
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);

  const handleSubmitPassword = (e) => {
    e.preventDefault();
    if (passwordInput === STATS_PASSWORD) {
      setUnlocked(true);
      setPasswordError(false);
      try { sessionStorage.setItem('stiltech_stats_unlocked', '1'); } catch {}
    } else {
      setPasswordError(true);
      setPasswordInput('');
    }
  };

  const fetchAll = async () => {
    try {
      const [clicksRes, visitsRes] = await Promise.all([
        axios.get(`${API}/track/stats`),
        axios.get(`${API}/track/visits-stats`),
      ]);
      setCounters(clicksRes.data?.counters || []);
      setVisits(visitsRes.data || null);
      setError(null);
    } catch (e) {
      setError('Error al cargar las estadísticas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!unlocked) return;
    fetchAll();
    const interval = setInterval(fetchAll, 5000);
    return () => clearInterval(interval);
  }, [unlocked]);

  const formatLastClick = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleString('es-MX', { timeZone: 'America/Mexico_City' });
    } catch {
      return iso;
    }
  };

  const fmt = (n) => (n ?? 0).toLocaleString('es-MX');

  const visitCards = [
    { label: 'Total General', key: 'total', icon: '🌌', gradient: 'from-orange-300 to-amber-400' },
    { label: 'Hoy', key: 'today', icon: '☀️', gradient: 'from-cyan-300 to-blue-400' },
    { label: 'Últimos 7 días', key: 'last_7_days', icon: '📅', gradient: 'from-emerald-300 to-teal-400' },
    { label: 'Últimos 30 días', key: 'last_30_days', icon: '📆', gradient: 'from-pink-300 to-rose-400' },
  ];

  // Password gate screen
  if (!unlocked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white p-6" style={{
        background: 'radial-gradient(ellipse at 50% 30%, #3a1a05 0%, #1f0f04 35%, #120902 70%, #0a0603 100%)',
      }} data-testid="stats-password-gate">
        <div className="w-full max-w-sm">
          {/* Mascot */}
          <div className="flex justify-center mb-6">
            <div className="relative w-20 h-20">
              <div className="absolute inset-[-15px] rounded-full pointer-events-none" style={{
                background: 'radial-gradient(circle, rgba(249, 115, 22, 0.45) 0%, transparent 70%)',
                filter: 'blur(15px)',
              }} />
              <img src="/assets/ocbet/ocbet-emblem.png" alt="OCBET" className="relative w-full h-full object-contain" style={{
                filter: 'drop-shadow(0 0 12px rgba(249, 115, 22, 0.7))',
              }} />
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-xl font-black tracking-[0.2em] mb-1">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 to-amber-400">OCBET</span>
            </h1>
            <p className="text-[10px] text-gray-500 uppercase tracking-[0.25em]">Acceso restringido</p>
          </div>

          {/* Password form */}
          <form onSubmit={handleSubmitPassword} className="space-y-3">
            <div>
              <label className="block text-[10px] text-gray-400 uppercase tracking-wider mb-2">Contraseña</label>
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
                autoFocus
                className="w-full px-4 py-3 rounded-lg text-white font-mono tracking-widest text-center bg-[#0c0c14] border border-orange-500/25 focus:border-orange-400/60 focus:outline-none transition-colors"
                placeholder="••••••"
                data-testid="stats-password-input"
              />
              {passwordError && (
                <p className="text-[11px] text-red-400 mt-2 text-center" data-testid="stats-password-error">
                  Contraseña incorrecta
                </p>
              )}
            </div>

            <button
              type="submit"
              data-testid="stats-password-submit"
              className="w-full py-3 rounded-lg font-bold text-sm tracking-wider text-white transition-all duration-200 hover:scale-[1.01]"
              style={{
                background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.35) 0%, rgba(245, 158, 11, 0.35) 100%)',
                border: '1px solid rgba(249, 115, 22, 0.5)',
                boxShadow: '0 0 20px rgba(249, 115, 22, 0.2)',
              }}
            >
              ACCEDER
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-6 sm:p-10" style={{
      background: 'radial-gradient(ellipse at 50% 0%, #3a1a05 0%, #1f0f04 35%, #120902 70%, #0a0603 100%)',
    }} data-testid="stats-page">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <div className="w-14 h-14 rounded-xl overflow-hidden border border-orange-500/40 bg-[#0a0a14]">
            <img src="/assets/ocbet/ocbet-emblem.png" alt="OCBET" className="w-full h-full object-contain" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-black">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-300 to-amber-400">OCBET</span>
              <span className="text-white"> · Estadísticas</span>
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">Panel de visitas y clics · se actualiza cada 5s</p>
          </div>
        </div>

        {loading && (
          <div className="text-center py-12 text-gray-400" data-testid="stats-loading">Cargando…</div>
        )}

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 p-5 text-red-400 text-sm mb-6" data-testid="stats-error">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* SITE VISITS SECTION */}
            <div data-testid="visits-section">
              <h2 className="text-xs uppercase tracking-[0.25em] text-gray-500 mb-3 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                Visitas al sitio
              </h2>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                {visitCards.map((card) => (
                  <div
                    key={card.key}
                    className="rounded-xl border border-orange-500/20 bg-[#0c0c14]/80 backdrop-blur p-4 sm:p-5 hover:border-orange-400/40 transition-colors"
                    data-testid={`visit-card-${card.key}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[10px] uppercase tracking-wider text-gray-500">{card.label}</span>
                      <span className="text-lg opacity-70">{card.icon}</span>
                    </div>
                    <p className={`text-3xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r ${card.gradient}`}>
                      {fmt(visits?.[card.key])}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-10 text-center">
          <a href="/" className="text-xs text-gray-500 hover:text-orange-300 transition-colors" data-testid="stats-back-link">
            ← Volver al sitio
          </a>
        </div>
      </div>
    </div>
  );
};

export default StatsPage;
